import React from 'react';
import { Box, Typography } from '@/shared/design-system/primitives';
import { Utensils, Pill } from 'lucide-react';
import { formatMacro } from '@shared/utils/formatting';
import { SourceData } from '@/shared/logic/metabolic/nutrition-compute';
import { motion, AnimatePresence } from 'framer-motion';

interface SourceAuditListProps {
    sources: SourceData[];
    unit: string;
    color: string;
    isVisible: boolean;
}

/**
 * SourceAuditList: Inline expandable list of contributors for a specific nutrient.
 * V6.0: Replaces the ContributorModal with a seamless inline expansion.
 */
export const SourceAuditList: React.FC<SourceAuditListProps> = ({ 
    sources, unit, color, isVisible 
}) => {
    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                    style={{ overflow: 'hidden' }}
                >
                    <Box style={{ 
                        marginTop: 10, 
                        padding: 12, 
                        backgroundColor: 'rgba(0,0,0,0.4)', 
                        borderRadius: 12, 
                        borderWidth: 1, 
                        borderColor: 'rgba(255,255,255,0.05)'
                    }}>
                        {sources.length === 0 ? (
                            <Typography style={{ textAlign: 'center', color: '#444', fontSize: 10, padding: 15, fontStyle: 'italic' }}>
                                NO ACTIVE SOURCES RECORDED
                            </Typography>
                        ) : (
                            <Box style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {sources.sort((a, b) => b.amount - a.amount).map((src, idx) => (
                                    <Box key={idx} style={{ 
                                        padding: 10, borderRadius: 10, backgroundColor: 'rgba(255,255,255,0.01)', 
                                        flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
                                        borderWidth: 1, borderColor: 'rgba(255,255,255,0.02)'
                                    }}>
                                        <Box style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1 }}>
                                            <Box style={{ width: 24, height: 24, borderRadius: 6, backgroundColor: src.type === 'SUPPLEMENT' ? 'rgba(168, 85, 247, 0.1)' : 'rgba(46, 204, 113, 0.1)', alignItems: 'center', justifyContent: 'center' }}>
                                                {src.type === 'SUPPLEMENT' ? <Pill size={12} color="#a855f7" /> : <Utensils size={12} color="#2ecc71" />}
                                            </Box>
                                            <Box style={{ flex: 1 }}>
                                                <Typography style={{ fontSize: 11, fontWeight: '900', color: '#eee' }}>{src.label}</Typography>
                                                <Typography style={{ fontSize: 8, fontWeight: '800', color: '#444', textTransform: 'uppercase' }}>{src.type}</Typography>
                                            </Box>
                                        </Box>
                                        <Typography style={{ fontSize: 13, fontWeight: '950', color: '#fff', fontVariantNumeric: 'lining-nums' } as any}>
                                            {formatMacro(src.amount)}<Typography style={{ fontSize: 8, color: '#444' }}> {unit}</Typography>
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
